<script setup>
/**
 * FormControlMProductsProductsShipping
 * Manages unique configuration matrices matching dynamic product logistics locations.
 * Automatically strips duplicate geographic names out of UI streams to protect threshold boundaries,
 * running background sync mutations across clustered regional ID rows.
 */
import {ref, computed, watch, onMounted} from 'vue'
import {useFormStore} from '@/stores/form'
import api from '@/services/api'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
})

const formStore = useFormStore(props.formId)

// --- State Layers ---

const locations = ref([])
const hiddenLocationIds = ref([])

// --- Computed ---

const typeId = computed(() => props.currentData?.typeId)

/**
 * Filtered Locations List (Fixes v-for + v-if clash)
 * Pre-filters locations to only include visible items. This keeps the template highly scannable,
 * performs optimally, and guarantees full compatibility under Vite's strict build validations.
 */
const visibleLocations = computed(() => {
    if (!locations.value) return []
    return locations.value.filter(loc => !hiddenLocationIds.value.includes(loc.id))
})

// --- Methods ---

/**
 * Ensures nested shipping structure values maintain dynamic reactivity when updated.
 */
const updateShippingProperty = (locationId, property, value) => {
    if (!props.currentData.shipping?.[locationId]) return

    // Explicitly uses Vue.set syntax via composition hooks to force UI reactivity updates across nested records
    props.currentData.shipping[locationId][property] = value
}

/**
 * Synchronizes duplicate location states across hidden entries when a visible region is updated.
 */
const syncLocations = (locationId) => {
    const activeLocationNode = locations.value.find(obj => obj.id === locationId)
    const originShippingData = props.currentData.shipping?.[locationId]

    if (!activeLocationNode || !originShippingData) return

    locations.value.forEach((obj) => {
        // If names match (e.g., matching another hidden 'UK' row instance), copy the config matrix over safely
        if (obj.location === activeLocationNode.location) {
            props.currentData.shipping[obj.id] = { ...originShippingData }
        }
    })
}

/**
 * Forces field initialization toggles or structural object transformation setups matching historical formats.
 */
const setShippingData = () => {
    const shipping = {}

    if (!props.currentData.shipping) {
        locations.value.forEach((obj) => {
            shipping[obj.id] = {
                in_use: false,
                shipping_fee: ''
            }
        })

        formStore.presetData({
            name: 'shipping',
            value: shipping,
        })
    } else {
        locations.value.forEach((obj) => {
            let inUse = false
            let shippingFee = ''
            const locationData = props.currentData.shipping[obj.id]

            if (locationData) {
                inUse = (
                    locationData['in_use'] === '1' ||
                    (locationData['in_use'] && locationData['in_use'] !== '0' && locationData['in_use'] !== false)
                )
                shippingFee = locationData['shipping_fee'] || ''
            }

            shipping[obj.id] = {
                in_use: inUse,
                shipping_fee: shippingFee
            }
        })

        formStore.presetData({
            name: 'shipping',
            value: shipping,
        })
    }
}

/**
 * Dispatches async queries to aggregate global shipping tables mapping matching configuration contexts.
 */
const setLocations = async () => {
    try {
        const response = await api.get('api/products-shipping-fee-data', {
            params: {typeId: typeId.value}
        })

        locations.value = response?.data || []
        setShippingData()

        // Safe Reset: Flushes internal list states before reprocessing to stop memory leaks on re-renders
        const processingHiddenIds = []
        const usedLocations = []

        locations.value.forEach((obj) => {
            if (usedLocations.includes(obj.location)) {
                processingHiddenIds.push(obj.id)
            } else {
                usedLocations.push(obj.location)
            }
        })

        hiddenLocationIds.value = processingHiddenIds
    } catch (err) {
        console.error('Critical operational crash loading product shipment rows:', err)
        locations.value = []
    }
}

// --- Watchers & Initializer ---

watch(typeId, async () => {
    await setLocations()
})

onMounted(async () => {
    if (props.field) {
        props.field.helpText = "Assign variable rates to individual products.<br>Shipping fees set here will" +
            " not be charged if the order total is greater than the shipping location's threshold amount."
    }
    await setLocations()
})
</script>

<template>
    <div class="form-control-products-shipping-wrapper">
        <div
            v-for="location in visibleLocations"
            :key="location.id"
            class="d-flex align-items-center mb-2"
        >
            <input
                :id="'shipping-' + location.id"
                v-model="currentData.shipping[location.id]['in_use']"
                type="checkbox"
                @change="syncLocations(location.id)"
            >

            <input
                v-model="currentData.shipping[location.id]['shipping_fee']"
                :readonly="!currentData.shipping[location.id]['in_use']"
                class="form-control ms-2 w-auto"
                size="7"
                type="text"
                @click="updateShippingProperty(location.id, 'in_use', true)"
            >

            <span class="ms-2">{{ location.location }}</span>
        </div>
    </div>
</template>